import { useState, type FormEvent } from "react";

export type ContactValues = {
  name: string;
  email: string;
  phone: string;
  city: string;
  situation: string;
  message: string;
  consent: boolean;
};

export type ContactStatus = "idle" | "submitting" | "success" | "error";
type Errors = Partial<Record<keyof ContactValues, string>>;

const initial: ContactValues = { name: "", email: "", phone: "", city: "", situation: "", message: "", consent: false };

export function useContactForm() {
  const [values, setValues] = useState<ContactValues>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<ContactStatus>("idle");

  const update = <K extends keyof ContactValues>(key: K, value: ContactValues[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): Errors => {
    const e: Errors = {};
    if (values.name.trim().length < 2) e.name = "Indiquez votre nom complet.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = "Adresse e-mail invalide.";
    if (!/^[+\d][\d\s.-]{8,}$/.test(values.phone.trim())) e.phone = "Numéro de téléphone invalide.";
    if (!values.city.trim()) e.city = "Indiquez votre ville ou secteur.";
    if (!values.situation) e.situation = "Choisissez votre situation.";
    if (!values.consent) e.consent = "Votre accord est nécessaire pour être recontacté.";
    return e;
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      setStatus("error");
      return;
    }
    setStatus("submitting");
    await new Promise((r) => setTimeout(r, 1200));
    setStatus("success");
  };

  const reset = () => {
    setValues(initial);
    setErrors({});
    setStatus("idle");
  };

  return { values, errors, status, update, submit, reset };
}
